import React from 'react';
import { connect } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, Grid } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import DialogCloseButton from '../../../buttons/DialogCloseButton';
import UploadMediaButton from './common/UploadMediaButton';
import { dataURLtoBlob } from '../../../../utilities/fileUtilities';
import ImageElementStockImagesSearch from './sources/image-element/stock-images/ImageElementStockImagesSearch';
import ImageElementStockImagesContainer from './sources/image-element/stock-images/ImageElementStockImagesContainer';
import ImageElementAssets from './tabs/image-element/ImageElementAssets';
import ImageElementPickerTabs from './tabs/image-element/ImageElementPickerTabs';
import { mediaPickerActions } from './store';
import MediaPickersConstants from './MediaPickersConstants';
import { ELEMENTS_PANEL_TABS, MEDIA_RESOURCE_TYPE, MEDIA_SOURCE_TYPE, MEDIA_USAGE_TYPE } from '../CreatePageConstants';
import { createMediaSource, createMediaSourceDataForImage } from '../helpers';
import { createActions } from '../store';
import { trackEvent, AnalyticsConstants } from '../../../../utilities/analyticsUtilities';

const styles = theme => ({
    imagesLibrarySection: {
        width: '100%',
        padding: `${theme.spacing(3, 0, 0, 0)} !important`,
        marginLeft: -theme.spacing(6),
        marginRight: -theme.spacing(6)
    },
    imagesDialogContent: {
        overflowY: 'hidden',
        padding: theme.spacing(1, 2)
    }
});

// Picker for the image elements of the Elements panel. Unlike the logo and backdrop pickers
// it applies the picked media as is, without a cropping step.
const ImageElementMediaPickerDialog = (props) => {
    const {
        classes, imageElementMediaPickerOpen, imageElementPickerSelectedTab, imageElementPickerTargetIndex,
        setProperty, loadDefaultImageElementStockImages, loadUserImageElementAssets, addImageElement, updateImageElement,
        selectImagesTab, uploadImageElementDirectly, videoSettingsId
    } = props;

    const closeDialog = () => setProperty('imageElementMediaPickerOpen', false);

    // Every gallery funnels through here, so the source the picture came from is passed in
    // rather than derived: an uploaded file and a picked asset both arrive as an IMAGE media
    // source and could not be told apart afterwards.
    const applyMediaSource = (mediaSource, source) => {
        if (imageElementPickerTargetIndex === null) {
            addImageElement(mediaSource);

            // The picture can be picked from either tab, and landing back on the one it is not on
            // reads as though nothing happened.
            selectImagesTab();
        }
        else
            updateImageElement(imageElementPickerTargetIndex, { mediaSource });

        trackEvent('Assigned Media', {
            category: AnalyticsConstants.VIDEO_CATEGORY,
            type: 'image_element',
            source: source,
            videoId: videoSettingsId
        });

        closeDialog();
    };

    const onStockImageSelect = (stockImage) => {
        const mediaData = createMediaSourceDataForImage(stockImage.originalUrl);
        applyMediaSource(createMediaSource(mediaData, MEDIA_SOURCE_TYPE.STOCK_IMAGE, stockImage.id, MEDIA_RESOURCE_TYPE.IMAGE), 'stock');
    };

    const onUploadedImageSelect = async (dataURL) => {

        closeDialog();

        const imageBlob = dataURLtoBlob(dataURL);
        const uploadedFile = await uploadImageElementDirectly(imageBlob);

        const activeUrl = uploadedFile ? uploadedFile.url : URL.createObjectURL(imageBlob);
        const mediaData = createMediaSourceDataForImage(activeUrl);
        const mediaSource = createMediaSource(mediaData, MEDIA_SOURCE_TYPE.UPLOADED, null, MEDIA_RESOURCE_TYPE.IMAGE);
        if (uploadedFile)
            mediaSource.directUpload = uploadedFile;

        applyMediaSource(mediaSource, 'upload');
    };

    const onUserAssetSelect = (userAsset) => {
        const mediaData = createMediaSourceDataForImage(userAsset.originalUrl);
        applyMediaSource(createMediaSource(mediaData, MEDIA_SOURCE_TYPE.USER_ASSET, userAsset.id, MEDIA_RESOURCE_TYPE.IMAGE), 'asset');
    };

    const onDialogEnter = () => {
        // The dialog opens on My Assets now, so only the gallery actually on screen is fetched.
        if (imageElementPickerSelectedTab === MediaPickersConstants.MEDIA_PICKER_TABS.MY_ASSETS)
            loadUserImageElementAssets(1, MediaPickersConstants.USER_ASSETS_PER_PAGE);
        else
            loadDefaultImageElementStockImages();

        trackEvent('Opened Media Picker', {
            category: AnalyticsConstants.VIDEO_CATEGORY,
            type: 'image'
        });
    };

    const renderTabContent = () => {
        switch (imageElementPickerSelectedTab) {
            case MediaPickersConstants.MEDIA_PICKER_TABS.IMAGES:
                return <ImageElementStockImagesContainer onImageSelect={onStockImageSelect} />;
            case MediaPickersConstants.MEDIA_PICKER_TABS.MY_ASSETS:
                return <ImageElementAssets onAssetSelect={onUserAssetSelect} />;
            default:
                return null;
        }
    };

    return (
        <Dialog
            open={imageElementMediaPickerOpen}
            onClose={closeDialog}
            maxWidth='md'
            fullWidth={true}
            scroll='body'
            aria-labelledby='image-media-picker-dialog'
            TransitionProps={{ onEnter: onDialogEnter }}
        >
            <DialogTitle id='image-media-picker-dialog'>
                Image
                <DialogCloseButton onClick={closeDialog} />
            </DialogTitle>
            <DialogContent className={classes.imagesDialogContent}>
                <Grid
                    container
                    direction='column'
                    justifyContent='center'
                    alignItems='center'
                    spacing={4}
                >
                    <Grid>
                        <UploadMediaButton
                            title='Upload Image'
                            mediaFormat={'png'}
                            mediaUsageType={MEDIA_USAGE_TYPE.IMAGE_ELEMENT}
                            uploadMimeTypes={MediaPickersConstants.IMAGE_ELEMENT_UPLOAD_MIME_TYPES}
                            onImageSelected={onUploadedImageSelect}
                        />
                    </Grid>

                    <Grid className={classes.imagesLibrarySection}>
                        <Grid
                            container
                            direction='column'
                            spacing={0}
                        >
                            <Grid>
                                <Grid
                                    container
                                    direction='row'
                                    justifyContent='space-between'
                                    alignItems='center'
                                >
                                    <ImageElementPickerTabs />
                                    <Grid style={{ flexGrow: 1 }}></Grid>
                                    {imageElementPickerSelectedTab === MediaPickersConstants.MEDIA_PICKER_TABS.IMAGES && <ImageElementStockImagesSearch />}
                                </Grid>
                            </Grid>

                            {renderTabContent()}
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

const mapStateToProps = (state) => {
    const {
        imageElementMediaPickerOpen, imageMediaPickerOpen,
        imageElementPickerSelectedTab, imagePickerSelectedTab,
        imageElementPickerTargetIndex, imagePickerTargetIndex
    } = state.mediaPicker;

    const { id: videoSettingsId } = state.createPreview.present.videoSettings;

    return {
        imageElementMediaPickerOpen: imageElementMediaPickerOpen ?? imageMediaPickerOpen,
        imageElementPickerSelectedTab: imageElementPickerSelectedTab ?? imagePickerSelectedTab,
        imageElementPickerTargetIndex: imageElementPickerTargetIndex ?? imagePickerTargetIndex,
        videoSettingsId
    };
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(mediaPickerActions.setProperty(key, value));
    const loadDefaultImageElementStockImages = () => dispatch(mediaPickerActions.loadDefaultImageElementStockImages());
    const loadUserImageElementAssets = (pageNumber, itemsPerPage) => dispatch(mediaPickerActions.loadUserImageElementAssets(pageNumber, itemsPerPage));
    const addImageElement = (mediaSource) => dispatch(createActions.addImageElement(mediaSource));
    const updateImageElement = (index, properties) => dispatch(createActions.updateImageElement(index, properties));
    const selectImagesTab = () => dispatch(createActions.setProperty('elementsSelectedTab', ELEMENTS_PANEL_TABS.IMAGE_ELEMENTS));
    const uploadImageElementDirectly = (imageBlob) => dispatch(createActions.uploadImageElementDirectly(imageBlob));

    return {
        setProperty,
        loadDefaultImageElementStockImages,
        loadUserImageElementAssets,
        addImageElement,
        updateImageElement,
        selectImagesTab,
        uploadImageElementDirectly
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ImageElementMediaPickerDialog, styles));
